/**
 * Form body for the Status Update modal (reason, additional info, comments).
 * @module StatusUpdateModalBody
 */

import {
  SearchableSelect,
  useModalPortalTarget,
} from "@risalabs_frontend_org/oasis-ui-kit";
import { RefObject } from "react";
import AdditionalInformationSection from "./AdditionalInformationSection";
import { StatusUpdateModalBodyProps } from "./statusUpdateModal.types";

/**
 * Form fields for the status-update modal.
 *
 * Must render as a child of Modal so {@link useModalPortalTarget}
 * resolves the modal portal node and dropdowns stack above the backdrop.
 * The additional-information block is shown only when
 * `shouldShowAdditionalInformation` is true; `isUserInputRequired` controls
 * whether those fields are marked required.
 *
 * @input props - Controlled form state, visibility/required flags, and modal
 *   metadata
 * @return JSX for the status-update form body
 */
const StatusUpdateModalBody = ({
  reason,
  setReason,
  textareaContent,
  setTextareaContent,
  preferredDrugForm,
  onPreferredJcodeEntriesChange,
  setPreferredDrugValues,
  setStepTherapyCount,
  setSource,
  metaData,
  shouldShowAdditionalInformation,
  isUserInputRequired,
}: StatusUpdateModalBodyProps) => {
  const portalRef = useModalPortalTarget();

  return (
    <div className="flex flex-col gap-3">
      <SearchableSelect
        id="status-update"
        label={metaData?.label || "Reason for Query"}
        options={metaData?.options || []}
        onOptionChange={(option) => {
          setReason(option.value);
        }}
        placeholder={metaData?.placeholder || "Select the Reason for Query"}
        value={reason ?? undefined}
        targetRef={portalRef}
        usePortal={true}
      />
      {shouldShowAdditionalInformation && (
        <AdditionalInformationSection
          preferredDrugForm={preferredDrugForm}
          onPreferredJcodeEntriesChange={onPreferredJcodeEntriesChange}
          setPreferredDrugValues={setPreferredDrugValues}
          setStepTherapyCount={setStepTherapyCount}
          setSource={setSource}
          targetRef={portalRef as RefObject<HTMLElement>}
          isUserInputRequired={isUserInputRequired}
        />
      )}
      <div className="mb-2 flex flex-col gap-2">
        <div className="text-tiny font-normal text-primaryGray-2">Comments</div>
        <textarea
          placeholder="Enter additional details..."
          value={textareaContent}
          onChange={(e) => setTextareaContent(e.target.value)}
          className="h-20 w-full resize-none rounded-md border border-primaryGray-14 p-3 text-small placeholder-gray-400 focus:outline-none"
        />
      </div>
    </div>
  );
};

export default StatusUpdateModalBody;
